import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  ArrowLeft, Trophy, Users, Globe, Flame, Target, TrendingUp, 
  TrendingDown, Minus, Award, Zap, Plane, Star, Brain, Rocket
} from "lucide-react";

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scope, setScope] = useState("class");
  const [period, setPeriod] = useState("weekly");
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // V2 State
  const [activeTab, setActiveTab] = useState("xp");
  const [v2Data, setV2Data] = useState({ xp: null, skill: null, evolution: null, me: null });
  const [v2Loading, setV2Loading] = useState(false);

  useEffect(() => {
    if (activeTab === "classic") {
      fetchLeaderboard();
    }
  }, [scope, period, activeTab]);
  
  // V2: Fetch data when tab changes
  useEffect(() => {
    if (activeTab !== "classic") {
      fetchV2Data(activeTab);
    }
  }, [activeTab]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leaderboard`, {
        params: { scope, period },
        withCredentials: true
      });
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      toast.error("Erro ao carregar leaderboard");
    } finally {
      setLoading(false);
    }
  };
  
  // V2: Fetch V2 leaderboard data
  const fetchV2Data = async (type) => {
    setV2Loading(true);
    try {
      const [dataRes, meRes] = await Promise.all([
        axios.get(`${API}/leaderboard/v2/${type}`, { withCredentials: true }),
        v2Data.me ? Promise.resolve({ data: v2Data.me }) : axios.get(`${API}/leaderboard/v2/me`, { withCredentials: true })
      ]);
      setV2Data(prev => ({ 
        ...prev, 
        [type]: dataRes.data,
        me: meRes.data
      }));
    } catch (error) {
      console.error("Error fetching V2 leaderboard:", error);
    } finally {
      setV2Loading(false);
    }
  };

  const getPeriodLabel = (p) => {
    switch (p) {
      case "weekly": return "Esta Semana";
      case "monthly": return "Este Mês";
      case "all-time": return "Todos os Tempos";
      default: return p;
    }
  };

  const getMedalIcon = (position) => {
    switch (position) {
      case 1: 
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-yellow-400 to-amber-600 flex items-center justify-center border-2 border-yellow-300 shadow-lg shadow-yellow-500/30">
            <Trophy className="w-4 h-4 text-white" />
          </div>
        );
      case 2: 
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-slate-300 to-slate-500 flex items-center justify-center border-2 border-slate-200 shadow-lg shadow-slate-400/30">
            <Star className="w-4 h-4 text-white" />
          </div>
        );
      case 3: 
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-orange-400 to-orange-700 flex items-center justify-center border-2 border-orange-300 shadow-lg shadow-orange-500/30">
            <Plane className="w-4 h-4 text-white" />
          </div>
        );
      default: 
        return (
          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">
            <span className="text-sm font-mono text-slate-400">{position}</span>
          </div>
        );
    }
  };

  const getPositionStyle = (position) => {
    switch (position) {
      case 1: return "bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-yellow-500/50";
      case 2: return "bg-gradient-to-r from-slate-400/20 to-slate-300/10 border-slate-400/50";
      case 3: return "bg-gradient-to-r from-orange-600/20 to-amber-600/10 border-orange-600/50";
      default: return "bg-slate-900/50 border-slate-700";
    }
  };

  if (loading && activeTab === "classic") {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-chivo text-lg text-cockpit-text-bright flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  Leaderboard
                </h1>
                <p className="text-xs text-slate-500 font-mono">
                  Rankings e Classificações
                </p>
              </div>
            </div>
            
            {/* Period Selector - Only show for classic mode */}
            {activeTab === "classic" && (
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-40 input-cockpit" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="weekly">Esta Semana</SelectItem>
                  <SelectItem value="monthly">Este Mês</SelectItem>
                  {scope === "global" && (
                    <SelectItem value="all-time">Todos os Tempos</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* V2 Tabs - Main Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-4 bg-cockpit-panel">
            <TabsTrigger 
              value="xp" 
              className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400"
              data-testid="tab-xp"
            >
              <Zap className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">XP Semanal</span>
              <span className="sm:hidden text-xs">XP</span>
            </TabsTrigger>
            <TabsTrigger 
              value="skill"
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
              data-testid="tab-skill"
            >
              <Brain className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Skill Rating</span>
              <span className="sm:hidden text-xs">Skill</span>
            </TabsTrigger>
            <TabsTrigger 
              value="evolution"
              className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
              data-testid="tab-evolution"
            >
              <Rocket className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Evolução</span>
              <span className="sm:hidden text-xs">Evol.</span>
            </TabsTrigger>
            <TabsTrigger 
              value="classic"
              className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400"
              data-testid="tab-classic"
            >
              <Trophy className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Clássico</span>
              <span className="sm:hidden text-xs">Class.</span>
            </TabsTrigger>
          </TabsList>
          
          {/* V2: XP Leaderboard */}
          <TabsContent value="xp" className="mt-6">
            <V2LeaderboardSection 
              type="xp"
              data={v2Data.xp}
              me={v2Data.me}
              loading={v2Loading}
              user={user}
              getMedalIcon={getMedalIcon}
              getPositionStyle={getPositionStyle}
            />
          </TabsContent>
          
          {/* V2: Skill Rating Leaderboard */}
          <TabsContent value="skill" className="mt-6">
            <V2LeaderboardSection 
              type="skill"
              data={v2Data.skill}
              me={v2Data.me}
              loading={v2Loading}
              user={user}
              getMedalIcon={getMedalIcon}
              getPositionStyle={getPositionStyle}
            />
          </TabsContent>
          
          {/* V2: Evolution Leaderboard */}
          <TabsContent value="evolution" className="mt-6">
            <V2LeaderboardSection 
              type="evolution"
              data={v2Data.evolution}
              me={v2Data.me}
              loading={v2Loading}
              user={user}
              getMedalIcon={getMedalIcon}
              getPositionStyle={getPositionStyle}
            />
          </TabsContent>
          
          {/* Classic Leaderboard */}
          <TabsContent value="classic" className="mt-6">
            <ClassicLeaderboard 
              scope={scope}
              setScope={setScope}
              leaderboard={leaderboard}
              user={user}
              period={period}
              getMedalIcon={getMedalIcon}
              getPositionStyle={getPositionStyle}
              getPeriodLabel={getPeriodLabel}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// V2 Leaderboard Section Component
const V2LeaderboardSection = ({ type, data, me, loading, user, getMedalIcon, getPositionStyle }) => {
  const config = {
    xp: {
      title: "XP Semanal",
      description: "XP ganho nos últimos 7 dias através de treinos e exames",
      color: "purple",
      Icon: Zap,
      getValue: (item) => item.xp,
      formatValue: (val) => `${val} XP`,
      getMyValue: (me) => me?.weekly_xp || 0
    },
    skill: {
      title: "Skill Rating",
      description: "Rating de habilidade baseado na sua precisão (estilo Elo)",
      color: "cyan",
      Icon: Brain,
      getValue: (item) => item.skill_rating,
      formatValue: (val) => val,
      getMyValue: (me) => me?.skill_rating || 1000
    },
    evolution: {
      title: "Evolução",
      description: "Melhoria da precisão: últimos 7 dias vs semana anterior",
      color: "emerald",
      Icon: Rocket,
      getValue: (item) => item.evolution,
      formatValue: (val) => `${val >= 0 ? '+' : ''}${val}%`,
      getMyValue: (me) => me?.evolution || 0
    }
  };
  
  const c = config[type];
  const { title, description, color, Icon, getValue, formatValue, getMyValue } = c;
  
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={`w-8 h-8 border-4 border-${color}-500 border-t-transparent rounded-full animate-spin`}></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Description */}
      <Card className="instrument-panel">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Icon className={`w-5 h-5 text-${color}-400`} />
            <div>
              <h3 className="font-chivo text-cockpit-text-bright">{title}</h3>
              <p className="text-xs text-slate-400">{description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* My Stats Card */}
      {me && (
        <Card className={`instrument-panel border-${color}-500/30`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getMedalIcon(data?.user_position || 0)}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">A sua posição</p>
                  <span className="font-chivo text-2xl text-cockpit-text-bright">
                    #{data?.user_position || '-'}
                  </span>
                  <p className="text-sm text-slate-400">
                    de {data?.total_participants || 0} participantes
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className={`font-mono text-3xl text-${color}-400`}>
                  {formatValue(getMyValue(me))}
                </div>
                {type === "evolution" && me && (
                  <p className="text-xs text-slate-500 mt-1">
                    {me.previous_accuracy}% → {me.recent_accuracy}%
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Rankings Table */}
      {data?.rankings?.length > 0 && (
        <Card className="instrument-panel">
          <CardHeader className="pb-4">
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <Icon className={`w-5 h-5 text-${color}-400`} />
              Top 50 - {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.rankings.map((entry) => (
                <div 
                  key={entry.user_id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${getPositionStyle(entry.position)} ${entry.user_id === user?.user_id ? `ring-2 ring-${color}-500/50` : ''}`}
                >
                  <div className="flex items-center gap-3">
                    {getMedalIcon(entry.position)}
                    <div className="flex items-center gap-3">
                      {entry.picture ? (
                        <img src={entry.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                          <span className="text-xs text-slate-400">{entry.name?.charAt(0) || '?'}</span>
                        </div>
                      )}
                      <span className={`font-medium ${entry.user_id === user?.user_id ? `text-${color}-400` : 'text-cockpit-text-bright'}`}>
                        {entry.name}
                      </span>
                    </div>
                  </div>
                  <div className={`font-mono text-lg text-${color}-400 flex items-center gap-2`}>
                    {formatValue(getValue(entry))}
                    {type === "evolution" && (
                      <span>
                        {entry.evolution > 0 ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : 
                         entry.evolution < 0 ? <TrendingDown className="w-4 h-4 text-red-400" /> : 
                         <Minus className="w-4 h-4 text-slate-400" />}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {(!data?.rankings || data.rankings.length === 0) && !loading && (
        <Card className="instrument-panel">
          <CardContent className="p-8 text-center">
            <Icon className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">Ainda não há dados suficientes para o ranking.</p>
            <p className="text-sm text-slate-500 mt-2">Continue a treinar para aparecer aqui!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Classic Leaderboard Component
const ClassicLeaderboard = ({ scope, setScope, leaderboard, user, period, getMedalIcon, getPositionStyle, getPeriodLabel }) => {
  return (
    <div className="space-y-6">
      {/* Scope Tabs */}
      <Tabs value={scope} onValueChange={setScope}>
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-cockpit-panel">
          <TabsTrigger 
            value="class" 
            className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400"
            data-testid="tab-class"
          >
            <Users className="w-4 h-4 mr-2" />
            Minha Turma
          </TabsTrigger>
          <TabsTrigger 
            value="global"
            className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
            data-testid="tab-global"
          >
            <Globe className="w-4 h-4 mr-2" />
            Global
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Score Formula Info */}
      <Card className="instrument-panel">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-slate-400">Actividade (35%)</span>
            </div>
            <span className="text-slate-600">+</span>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-cyan-400" />
              <span className="text-slate-400">Qualidade (30%)</span>
            </div>
            <span className="text-slate-600">+</span>
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-slate-400">Consistência (25%)</span>
            </div>
            <span className="text-slate-600">+</span>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-yellow-400" />
              <span className="text-slate-400">Exames (10%)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User's Position Card */}
      {leaderboard?.user_score && (
        <Card className="instrument-panel border-orange-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getMedalIcon(leaderboard.user_position)}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">A sua posição</p>
                  <span className="font-chivo text-2xl text-cockpit-text-bright">
                    {leaderboard.user_score.name}
                  </span>
                  <p className="text-sm text-slate-400">
                    {leaderboard.user_score.rank?.name} · {leaderboard.user_score.current_streak} dias de streak
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-3xl text-orange-400 glow-orange">
                  {Math.round(leaderboard.user_score.total_score)}
                </div>
                <p className="text-xs text-slate-500">pontos totais</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Class Warning */}
      {scope === "class" && !user?.class_id && (
        <Card className="instrument-panel">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-cockpit-text-bright mb-2">
              Sem Turma Atribuída
            </h3>
            <p className="text-sm text-slate-400">
              Contacte o seu instrutor para ser adicionado a uma turma e ver o ranking da mesma.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Table */}
      {leaderboard?.rankings?.length > 0 && (
        <Card className="instrument-panel">
          <CardHeader className="pb-4">
            <CardTitle className="font-chivo text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                {scope === "class" ? (
                  <>
                    <Users className="w-5 h-5 text-orange-400" />
                    {leaderboard.class_info?.name || "Turma"}
                  </>
                ) : (
                  <>
                    <Globe className="w-5 h-5 text-cyan-400" />
                    Ranking Global
                  </>
                )}
              </span>
              <span className="text-sm font-normal text-slate-500">
                {leaderboard.total_participants} participantes · {getPeriodLabel(period)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.rankings.map((entry) => (
                <LeaderboardEntry 
                  key={entry.user_id}
                  entry={entry}
                  isCurrentUser={entry.user_id === user?.user_id}
                  getMedalIcon={getMedalIcon}
                  getPositionStyle={getPositionStyle}
                />
              ))}
            </div>
            
            {/* Show user if not in top rankings */}
            {leaderboard.user_position > leaderboard.rankings.length && leaderboard.user_score && (
              <>
                <div className="flex items-center gap-2 my-4 text-slate-500">
                  <div className="flex-1 h-px bg-slate-700"></div>
                  <span className="text-xs">...</span>
                  <div className="flex-1 h-px bg-slate-700"></div>
                </div>
                <LeaderboardEntry 
                  entry={leaderboard.user_score}
                  isCurrentUser={true}
                  getMedalIcon={getMedalIcon}
                  getPositionStyle={getPositionStyle}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {leaderboard?.rankings?.length === 0 && scope !== "class" && (
        <Card className="instrument-panel">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-cockpit-text-bright mb-2">
              Sem Rankings
            </h3>
            <p className="text-sm text-slate-400">
              Comece a treinar para aparecer no leaderboard!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const LeaderboardEntry = ({ entry, isCurrentUser, getMedalIcon, getPositionStyle }) => {
  return (
    <div 
      className={`p-3 sm:p-4 rounded-lg border transition-all ${getPositionStyle(entry.position)} ${
        isCurrentUser ? 'ring-2 ring-orange-500/50' : ''
      }`}
      data-testid={`leaderboard-entry-${entry.position}`}
    >
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Position */}
        <div className="w-8 sm:w-12 flex justify-center flex-shrink-0">
          {getMedalIcon(entry.position)}
        </div>
        
        {/* Name & Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium truncate text-sm sm:text-base ${isCurrentUser ? 'text-orange-400' : 'text-cockpit-text-bright'}`}>
              {entry.name}
            </span>
            {isCurrentUser && (
              <span className="text-[10px] sm:text-xs bg-orange-500/20 text-orange-400 px-1.5 sm:px-2 py-0.5 rounded">Você</span>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-500 flex-wrap">
            <span className="hidden sm:inline">{entry.rank?.name}</span>
            <span className="flex items-center gap-1">
              <Flame className="w-3 h-3 text-orange-400" />
              {entry.current_streak}d
            </span>
            <span className="flex items-center gap-1">
              <Target className="w-3 h-3 text-emerald-400" />
              {Math.round(entry.accuracy)}%
            </span>
          </div>
        </div>
        
        {/* Score */}
        <div className="text-right flex-shrink-0">
          <div className="font-mono text-lg sm:text-xl font-bold text-cockpit-text-bright">
            {Math.round(entry.total_score)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
